import { useEffect } from 'react'
import { isBrowser } from '@/lib/utils/tauri'
import { toast } from 'sonner'

const VERSION_TOAST_ID = 'web-version-update'
const DEFAULT_POLL_INTERVAL_MS = 60_000

interface VersionManifest {
  version?: unknown
}

interface WebVersionNotifierProps {
  currentVersion?: string
  enabled?: boolean
  pollIntervalMs?: number
}

export function WebVersionNotifier({
  currentVersion = import.meta.env.VITE_APP_VERSION,
  enabled = import.meta.env.PROD && isBrowser(),
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: WebVersionNotifierProps) {
  useEffect(() => {
    if (!enabled || !currentVersion) return

    let disposed = false
    let requestInFlight: Promise<void> | null = null

    const checkForUpdate = () => {
      if (requestInFlight) return requestInFlight

      requestInFlight = fetch('/version.json', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      })
        .then(async response => {
          if (!response.ok) return

          const manifest = (await response.json()) as VersionManifest
          const deployedVersion =
            typeof manifest.version === 'string' ? manifest.version.trim() : ''

          if (
            !disposed &&
            deployedVersion &&
            deployedVersion !== currentVersion
          ) {
            toast('A new version of Scrambl is available', {
              id: VERSION_TOAST_ID,
              description: 'Refresh to get the latest updates.',
              duration: Infinity,
              action: {
                label: 'Refresh',
                onClick: () => window.location.reload(),
              },
            })
          }
        })
        .catch(() => {
          // A failed update check should never interrupt the active session.
        })
        .finally(() => {
          requestInFlight = null
        })

      return requestInFlight
    }

    const handleFocus = () => {
      void checkForUpdate()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate()
      }
    }

    void checkForUpdate()
    const intervalId = window.setInterval(checkForUpdate, pollIntervalMs)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      disposed = true
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [currentVersion, enabled, pollIntervalMs])

  return null
}
